import React, { useCallback, useLayoutEffect, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface UnityGameDockAppProps {
  ariaLabel: string;
  /** Shown under the squircle (very small) */
  label?: string;
  icon: React.ReactNode;
  /** Headcount-style badge (top-right of squircle). */
  badge?: React.ReactNode;
  /** e.g. raised-hands indicator (bottom-left, opposite corner from `badge`). */
  secondaryBadge?: React.ReactNode;
  /** Visual highlight when popover/menu is open */
  active?: boolean;
  /** Toggle / pressed state (e.g. raise hand) */
  pressed?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  /** Background for the squircle (Tailwind classes) */
  tileClassName: string;
  /** Extra classes for the caption under the squircle (e.g. wider / wrap) */
  labelClassName?: string;
  dataTourId?: string;
  /** Popover or extra UI; use `.unity-dock-popover` for panel chrome */
  children?: React.ReactNode;
  wrapperClassName?: string;
  /**
   * Set on the portaled anchor so parents can treat clicks inside the popover as "inside"
   * (e.g. `target.closest('[data-unity-dock-popover-owner="…"]')`) — required when portaling to body.
   */
  popoverOwnerId?: string;
}

const POPOVER_GAP_PX = 12;

/**
 * Single “app” tile in the Unity game dock (squircle + optional caption).
 * When `children` are passed, they render in a portal to `document.body` with fixed positioning
 * above the tile. (The dock tray uses `transform`, which breaks viewport-fixed descendants.)
 */
const UnityGameDockApp: React.FC<UnityGameDockAppProps> = ({
  ariaLabel,
  label,
  icon,
  badge,
  secondaryBadge,
  active,
  pressed,
  onClick,
  disabled,
  tileClassName,
  labelClassName,
  dataTourId,
  children,
  wrapperClassName,
  popoverOwnerId,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ x: number; b: number } | null>(null);
  const hasPopover = children != null && children !== false;

  const updateCoords = useCallback(() => {
    const w = wrapperRef.current;
    if (!w || !hasPopover) {
      setCoords(null);
      return;
    }
    const r = w.getBoundingClientRect();
    setCoords({
      x: r.left + r.width / 2,
      b: window.innerHeight - r.top + POPOVER_GAP_PX,
    });
  }, [hasPopover]);

  // Do not depend on `children` identity — parents often pass fresh JSX each render, which would
  // retrigger this effect and setState every frame (maximum update depth exceeded).
  useLayoutEffect(() => {
    updateCoords();
  }, [updateCoords, hasPopover]);

  useEffect(() => {
    if (!hasPopover) return undefined;
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords, true);
    return () => {
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [hasPopover, updateCoords]);

  const popoverNode =
    hasPopover && coords != null && typeof document !== 'undefined' ? (
      <div
        className="unity-dock-popover-anchor"
        {...(popoverOwnerId ? { 'data-unity-dock-popover-owner': popoverOwnerId } : {})}
        style={{
          position: 'fixed',
          left: coords.x,
          bottom: coords.b,
          transform: 'translateX(-50%)',
          zIndex: 10001,
          pointerEvents: 'auto',
        }}
      >
        {children}
      </div>
    ) : null;

  return (
    <div
      ref={wrapperRef}
      className={`relative flex shrink-0 flex-col items-center unity-dock-app-wrapper ${wrapperClassName || ''}`}
    >
      <button
        type="button"
        data-tour-id={dataTourId}
        aria-label={ariaLabel}
        title={ariaLabel}
        aria-pressed={pressed !== undefined ? pressed : undefined}
        disabled={disabled}
        onClick={onClick}
        className="unity-dock-app-button group flex flex-col items-center gap-0.5 rounded-lg p-0.5 outline-none transition-transform focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-black/40 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <div
          className={`unity-dock-squircle relative flex h-12 w-12 items-center justify-center rounded-2xl shadow-md transition-transform duration-150 group-hover:scale-105 group-active:scale-95 ${tileClassName} ${
            active || pressed ? 'ring-2 ring-white/50 ring-offset-2 ring-offset-transparent' : ''
          }`}
        >
          <span className="text-white drop-shadow-sm [&>svg]:h-6 [&>svg]:w-6">{icon}</span>
          {badge != null && badge !== false && (
            <span className="unity-dock-badge absolute -right-0.5 -top-0.5 flex min-h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white shadow">
              {badge}
            </span>
          )}
          {secondaryBadge != null && secondaryBadge !== false && (
            <span className="unity-dock-badge-secondary absolute -bottom-0.5 -left-0.5 flex min-h-[1.125rem] min-w-[1.125rem] items-center justify-center gap-0.5 rounded-full bg-amber-500 px-0.5 text-[10px] font-bold text-white shadow ring-1 ring-amber-950/30">
              {secondaryBadge}
            </span>
          )}
        </div>
        {label ? (
          <span
            className={`max-w-[4.25rem] truncate text-center text-[10px] font-medium leading-tight text-white/85 ${labelClassName || ''}`}
          >
            {label}
          </span>
        ) : null}
      </button>
      {popoverNode ? createPortal(popoverNode, document.body) : null}
    </div>
  );
};

export default UnityGameDockApp;
